import { useMutation, useQuery, useQueryClient } from "react-query";
import {
    GetInventoryDashboard,
    GetInventoryItems,
    AddInventoryItem,
    GetInventoryItemById,
    UpdateInventoryItem,
    DeleteInventoryItem,
    GetInventoryItemHistory,
    GetInventoryTransactions,
    AddInventoryTransaction,
    GetInventoryPhysicalCounts,
    AddInventoryPhysicalCount,
    GetInventoryConsumers,
    AddInventoryConsumer,
    UpdateInventoryConsumer,
    DeleteInventoryConsumer,
    GetInventoryConsumptionTrend,
    GetInventoryConsumptionByFloor,
    GetInventoryFloorItemBreakdown,
    GetInventoryBreakageReport,
    GetInventoryLowStockReport,
    GetInventoryCountMismatchesReport,
    DryRunInventoryCsvImport,
    ApplyInventoryCsvImport,
} from "../services/backend";
import { Notify, NotifyError } from "../utils/functions";

export function useGetInventoryDashboard() {
    return useQuery(['inventory-dashboard'], () => GetInventoryDashboard(), {
        refetchOnWindowFocus: false,
        staleTime: 0, // Real-time data
        select: (data) => data?.data || {},
        onError: (error) => {
            NotifyError("Failed to fetch inventory dashboard", error);
            console.error("Error fetching inventory dashboard:", error);
        }
    });
}

export function useGetInventoryItems(filters = {}) {
    return useQuery(['inventory-items', filters], () => GetInventoryItems(filters), {
        refetchOnWindowFocus: false,
        staleTime: 0, // Real-time data
        select: (data) => data?.data?.items || [],
        onError: (error) => {
            NotifyError("Failed to fetch inventory items", error);
            console.error("Error fetching inventory items:", error);
        }
    });
}

export function useGetInventoryItemById(itemId) {
    return useQuery(['inventory-item', itemId], () => GetInventoryItemById(itemId), {
        enabled: !!itemId,
        refetchOnWindowFocus: false,
        staleTime: 0, // Real-time data
        select: (data) => data?.data || {},
        onError: (error) => {
            NotifyError("Failed to fetch item details", error);
            console.error("Error fetching item details:", error);
        }
    });
}

export function useAddInventoryItem() {
    const queryClient = useQueryClient();
    return useMutation(AddInventoryItem, {
        onSuccess: async () => {
            Notify("Item added successfully", "success");
            await Promise.all([
                queryClient.invalidateQueries('inventory-items'),
                queryClient.invalidateQueries('inventory-dashboard')
            ]);
        },
        onError: (error) => {
            NotifyError("Failed to add item", error);
            console.error("Error adding inventory item:", error);
        }
    });
}

export function useUpdateInventoryItem() {
    const queryClient = useQueryClient();
    return useMutation(({ itemId, data }) => UpdateInventoryItem(itemId, data), {
        onSuccess: async (_, variables) => {
            Notify("Item updated successfully", "success");
            const queries = [
                queryClient.invalidateQueries('inventory-items'),
                queryClient.invalidateQueries('inventory-dashboard')
            ];
            if (variables?.itemId) queries.push(queryClient.invalidateQueries(['inventory-item', variables.itemId]));
            await Promise.all(queries);
        },
        onError: (error) => {
            NotifyError("Failed to update item", error);
            console.error("Error updating inventory item:", error);
        }
    });
}

export function useDeleteInventoryItem() {
    const queryClient = useQueryClient();
    return useMutation(DeleteInventoryItem, {
        onSuccess: async () => {
            Notify("Item deactivated successfully", "success");
            await Promise.all([
                queryClient.invalidateQueries('inventory-items'),
                queryClient.invalidateQueries('inventory-dashboard')
            ]);
        },
        onError: (error) => {
            NotifyError("Failed to deactivate item", error);
            console.error("Error deactivating inventory item:", error);
        }
    });
}

export function useGetInventoryItemHistory(itemId) {
    return useQuery(['inventory-item-history', itemId], () => GetInventoryItemHistory(itemId), {
        enabled: !!itemId,
        refetchOnWindowFocus: false,
        staleTime: 0, // Real-time data
        select: (data) => data?.data?.histories || [],
        onError: (error) => {
            NotifyError("Failed to fetch item history", error);
            console.error("Error fetching item history:", error);
        }
    });
}

export function useGetInventoryTransactions(itemId, filters = {}) {
    return useQuery(['inventory-transactions', itemId, filters], () => GetInventoryTransactions(itemId, filters), {
        enabled: !!itemId,
        refetchOnWindowFocus: false,
        staleTime: 0, // Real-time data
        select: (data) => data?.data || { transactions: [], total: 0 },
        onError: (error) => {
            NotifyError("Failed to fetch transactions", error);
            console.error("Error fetching inventory transactions:", error);
        }
    });
}

export function useAddInventoryTransaction() {
    const queryClient = useQueryClient();
    return useMutation(({ itemId, data }) => AddInventoryTransaction(itemId, data), {
        onSuccess: async (_, variables) => {
            Notify("Entry logged successfully", "success");
            const { itemId } = variables || {};
            const queries = [
                queryClient.invalidateQueries('inventory-items'),
                queryClient.invalidateQueries('inventory-dashboard')
            ];
            if (itemId) {
                queries.push(queryClient.invalidateQueries(['inventory-item', itemId]));
                queries.push(queryClient.invalidateQueries(['inventory-transactions', itemId]));
            }
            await Promise.all(queries);
        },
        onError: (error) => {
            NotifyError(error?.response?.data?.message || "Failed to log entry", error);
            console.error("Error logging inventory transaction:", error);
        }
    });
}

export function useGetInventoryPhysicalCounts(itemId) {
    return useQuery(['inventory-physical-counts', itemId], () => GetInventoryPhysicalCounts(itemId), {
        enabled: !!itemId,
        refetchOnWindowFocus: false,
        staleTime: 0, // Real-time data
        select: (data) => data?.data?.counts || [],
        onError: (error) => {
            NotifyError("Failed to fetch physical counts", error);
            console.error("Error fetching inventory physical counts:", error);
        }
    });
}

export function useAddInventoryPhysicalCount() {
    const queryClient = useQueryClient();
    return useMutation(({ itemId, data }) => AddInventoryPhysicalCount(itemId, data), {
        onSuccess: (response, variables) => {
            const mismatch = response?.data?.count?.mismatch;
            Notify(mismatch ? "Count recorded — mismatch flagged" : "Count recorded — matches system stock", mismatch ? "warn" : "success");
            const { itemId } = variables || {};
            if (itemId) {
                queryClient.invalidateQueries(['inventory-physical-counts', itemId]);
                queryClient.invalidateQueries(['inventory-item', itemId]);
            }
            queryClient.invalidateQueries('inventory-count-mismatches-report');
        },
        onError: (error) => {
            NotifyError(error?.response?.data?.message || "Failed to record physical count", error);
            console.error("Error recording inventory physical count:", error);
        }
    });
}

export function useGetInventoryConsumers(filters = {}) {
    return useQuery(['inventory-consumers', filters], () => GetInventoryConsumers(filters), {
        refetchOnWindowFocus: false,
        staleTime: 0, // Real-time data
        select: (data) => data?.data?.consumers || [],
        onError: (error) => {
            NotifyError("Failed to fetch consumers", error);
            console.error("Error fetching inventory consumers:", error);
        }
    });
}

export function useAddInventoryConsumer() {
    const queryClient = useQueryClient();
    return useMutation(AddInventoryConsumer, {
        onSuccess: () => {
            Notify("Person added successfully", "success");
            queryClient.invalidateQueries('inventory-consumers');
        },
        onError: (error) => {
            NotifyError("Failed to add person", error);
            console.error("Error adding inventory consumer:", error);
        }
    });
}

export function useUpdateInventoryConsumer() {
    const queryClient = useQueryClient();
    return useMutation(({ consumerId, data }) => UpdateInventoryConsumer(consumerId, data), {
        onSuccess: () => {
            Notify("Person updated successfully", "success");
            queryClient.invalidateQueries('inventory-consumers');
        },
        onError: (error) => {
            NotifyError("Failed to update person", error);
            console.error("Error updating inventory consumer:", error);
        }
    });
}

export function useDeleteInventoryConsumer() {
    const queryClient = useQueryClient();
    return useMutation(DeleteInventoryConsumer, {
        onSuccess: () => {
            Notify("Person removed successfully", "success");
            queryClient.invalidateQueries('inventory-consumers');
        },
        onError: (error) => {
            NotifyError("Failed to remove person", error);
            console.error("Error removing inventory consumer:", error);
        }
    });
}

export function useGetInventoryConsumptionTrend(filters = {}) {
    return useQuery(['inventory-consumption-trend', filters], () => GetInventoryConsumptionTrend(filters), {
        refetchOnWindowFocus: false,
        staleTime: 0, // Real-time data
        select: (data) => data?.data?.trend || [],
        onError: (error) => {
            NotifyError("Failed to fetch consumption trend", error);
            console.error("Error fetching consumption trend:", error);
        }
    });
}

export function useGetInventoryConsumptionByFloor(filters = {}) {
    return useQuery(['inventory-consumption-by-floor', filters], () => GetInventoryConsumptionByFloor(filters), {
        refetchOnWindowFocus: false,
        staleTime: 0, // Real-time data
        select: (data) => data?.data?.byFloor || [],
        onError: (error) => {
            NotifyError("Failed to fetch consumption by floor", error);
            console.error("Error fetching consumption by floor:", error);
        }
    });
}

export function useGetInventoryFloorItemBreakdown(filters = {}) {
    return useQuery(['inventory-floor-item-breakdown', filters], () => GetInventoryFloorItemBreakdown(filters), {
        enabled: !!filters.floor,
        refetchOnWindowFocus: false,
        staleTime: 0, // Real-time data
        select: (data) => data?.data?.byItem || [],
        onError: (error) => {
            NotifyError("Failed to fetch floor item breakdown", error);
            console.error("Error fetching floor item breakdown:", error);
        }
    });
}

export function useGetInventoryBreakageReport(filters = {}) {
    return useQuery(['inventory-breakage-report', filters], () => GetInventoryBreakageReport(filters), {
        refetchOnWindowFocus: false,
        staleTime: 0, // Real-time data
        select: (data) => data?.data || { breakages: [], summary: [] },
        onError: (error) => {
            NotifyError("Failed to fetch breakage report", error);
            console.error("Error fetching breakage report:", error);
        }
    });
}

export function useGetInventoryLowStockReport() {
    return useQuery(['inventory-low-stock-report'], () => GetInventoryLowStockReport(), {
        refetchOnWindowFocus: false,
        staleTime: 0, // Real-time data
        select: (data) => data?.data || { lowStock: [], overDailyMax: [] },
        onError: (error) => {
            NotifyError("Failed to fetch low stock report", error);
            console.error("Error fetching low stock report:", error);
        }
    });
}

export function useGetInventoryCountMismatchesReport() {
    return useQuery(['inventory-count-mismatches-report'], () => GetInventoryCountMismatchesReport(), {
        refetchOnWindowFocus: false,
        staleTime: 0, // Real-time data
        select: (data) => data?.data?.mismatches || [],
        onError: (error) => {
            NotifyError("Failed to fetch count mismatches", error);
            console.error("Error fetching count mismatches report:", error);
        }
    });
}

export function useDryRunInventoryCsvImport() {
    return useMutation(DryRunInventoryCsvImport, {
        onError: (error) => {
            NotifyError("Failed to validate CSV", error);
            console.error("Error validating inventory CSV:", error);
        }
    });
}

export function useApplyInventoryCsvImport() {
    const queryClient = useQueryClient();
    return useMutation(ApplyInventoryCsvImport, {
        onSuccess: async () => {
            Notify("CSV import applied successfully", "success");
            await Promise.all([
                queryClient.invalidateQueries('inventory-items'),
                queryClient.invalidateQueries('inventory-dashboard')
            ]);
        },
        onError: (error) => {
            NotifyError(error?.response?.data?.message || "Failed to apply CSV import", error);
            console.error("Error applying inventory CSV import:", error);
        }
    });
}
